import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import ImpersonationBanner from "./components/ImpersonationBanner";
import { TenantProvider } from "./context/TenantContext";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "./components/ui/sonner";

// Pages
const LandingPage = React.lazy(() => import("./components/LandingPage"));
const LoginPage = React.lazy(() => import("./components/LoginPage"));
const RegisterPage = React.lazy(() => import("./components/RegisterPage"));
const Dashboard = React.lazy(() => import("./components/Dashboard"));
const CreateProfile = React.lazy(() => import("./components/CreateProfile"));
const ShareProfile = React.lazy(() => import("./components/ShareProfile"));
const PublicProfile = React.lazy(() => import("./components/PublicProfile"));
const Shop = React.lazy(() => import("./components/Shop"));
const EnterpriseDashboard = React.lazy(() => import("./components/EnterpriseDashboard"));
const Reels = React.lazy(() => import("./components/Reels"));
const CreateReel = React.lazy(() => import("./components/CreateReel"));
const Feed = React.lazy(() => import("./components/Feed"));
const Messages = React.lazy(() => import("./components/Messages"));
const PrivacySettings = React.lazy(() => import("./components/PrivacySettings"));
const ProfileCustomization = React.lazy(() => import("./components/ProfileCustomization"));
const SubscriptionUpgrade = React.lazy(() => import("./components/SubscriptionUpgrade"));
const SubscriptionSuccess = React.lazy(() => import("./components/SubscriptionSuccess"));
const SuperAdminDashboard = React.lazy(() => import("./components/SuperAdminDashboard"));
const ContinuitySettings = React.lazy(() => import("./components/ContinuitySettings"));
const FeatureSpotlight = React.lazy(() => import("./components/FeatureSpotlight"));
const GovernmentSecurity = React.lazy(() => import("./components/GovernmentSecurity"));
const DigitalIdentity = React.lazy(() => import("./components/DigitalIdentity"));
const TrustCenter = React.lazy(() => import("./components/TrustCenter"));
const VerifyIdentity = React.lazy(() => import("./components/VerifyIdentity"));
const ComplianceCenter = React.lazy(() => import("./components/admin/ComplianceCenter"));
const IntegrationsHub = React.lazy(() => import("./components/IntegrationsHub"));
const GateDeviceGuide = React.lazy(() => import("./components/GateDeviceGuide"));
const SecurityMonitor = React.lazy(() => import("./components/SecurityMonitor"));
const KnowledgeBase = React.lazy(() => import("./components/KnowledgeBase"));
const WedgePage = React.lazy(() => import("./components/WedgePage"));
const RolesPermissions = React.lazy(() => import("./components/RolesPermissions"));
const AuditLog = React.lazy(() => import("./components/AuditLog"));
const OrgAdmin = React.lazy(() => import("./components/OrgAdmin"));
const Pricing = React.lazy(() => import("./components/Pricing"));
const PayPalReturn = React.lazy(() => import("./components/PayPalReturn"));
const InstitutionalLicensing = React.lazy(() => import("./components/InstitutionalLicensing"));
const BundleFunnel = React.lazy(() => import("./components/BundleFunnel"));
const VaultPlans = React.lazy(() => import("./components/VaultPlans"));
const Community = React.lazy(() => import("./components/Community"));
const B2BHub = React.lazy(() => import("./components/B2BHub"));
const B2BConsole = React.lazy(() => import("./components/admin/B2BConsole"));
const InvestorDashboard = React.lazy(() => import("./components/InvestorDashboard"));
const EventsHub = React.lazy(() => import("./components/EventsHub"));
const EventManager = React.lazy(() => import("./components/EventManager"));
const PublicEventRegister = React.lazy(() => import("./components/PublicEventRegister"));
const EventsLogin = React.lazy(() => import("./components/EventsLogin"));
const EventStaffCheckin = React.lazy(() => import("./components/EventStaffCheckin"));
const AttendeeTicket = React.lazy(() => import("./components/AttendeeTicket"));
const GeofencingAnalytics = React.lazy(() => import("./components/admin/GeofencingAnalytics"));
const SocialListeningAnalytics = React.lazy(() => import("./components/admin/SocialListeningAnalytics"));
const MarketplaceAnalytics = React.lazy(() => import("./components/admin/MarketplaceAnalytics"));
const PlatformSettings = React.lazy(() => import("./components/admin/PlatformSettings"));
const SubscriptionManagement = React.lazy(() => import("./components/admin/SubscriptionManagement"));
const WhiteLabelManagement = React.lazy(() => import("./components/admin/WhiteLabelManagement"));
const TenantManager = React.lazy(() => import("./components/admin/TenantManager"));
const WelcomeTour = React.lazy(() => import("./components/WelcomeTour"));
const InstallApp = React.lazy(() => import("./components/InstallApp"));
const CMSManager = React.lazy(() => import("./components/admin/CMSManager"));
const AdManager = React.lazy(() => import("./components/admin/AdManager"));
const DataMining = React.lazy(() => import("./components/admin/DataMining"));
const GeofencingSocialListening = React.lazy(() => import("./components/admin/GeofencingSocialListening"));
const NotificationSettings = React.lazy(() => import("./components/NotificationSettings"));
const ForgotPassword = React.lazy(() => import("./components/ForgotPassword"));
const ResetPassword = React.lazy(() => import("./components/ResetPassword"));
const TeamMemberSetup = React.lazy(() => import("./components/TeamMemberSetup"));
const TeamManagement = React.lazy(() => import("./components/TeamManagement"));
const PitchLibertyBank = React.lazy(() => import("./components/PitchLibertyBank"));
const PitchCountyEms = React.lazy(() => import("./components/PitchCountyEms"));
const PitchMilwaukeeDhhs = React.lazy(() => import("./components/PitchMilwaukeeDhhs"));
const ResourceFolder = React.lazy(() => import("./components/ResourceFolder"));
const ResourceFoldersAdmin = React.lazy(() => import("./components/admin/ResourceFoldersAdmin"));
const PitchEventCheckin = React.lazy(() => import("./components/PitchEventCheckin"));
const PitchDigitalCard = React.lazy(() => import("./components/PitchDigitalCard"));
const TeamMemberDashboard = React.lazy(() => import("./components/TeamMemberDashboard"));
const CookiePolicy = React.lazy(() => import("./components/CookiePolicy"));
const SiteSearch = React.lazy(() => import("./components/SiteSearch"));
const FamilyTracker = React.lazy(() => import("./components/FamilyTracker"));
const SessionPoster = React.lazy(() => import("./components/SessionPoster"));
const SessionCheckin = React.lazy(() => import("./components/SessionCheckin"));
const TermsTeamMember = React.lazy(() => import("./components/TermsTeamMember"));
const TermsOfService = React.lazy(() => import("./components/TermsOfService"));
const PrivacyPolicy = React.lazy(() => import("./components/PrivacyPolicy"));
const Support = React.lazy(() => import("./components/Support"));
const Advertise = React.lazy(() => import("./components/Advertise"));
const InvestorPortal = React.lazy(() => import("./components/InvestorPortal"));
const PitchSummary = React.lazy(() => import("./components/PitchSummary"));
const InvestorDataRoom = React.lazy(() => import("./components/InvestorDataRoom"));
const GradWall = React.lazy(() => import("./components/learning/GradWall"));
const InvestorAdmin = React.lazy(() => import("./components/admin/InvestorAdmin"));
const FeatureInsights = React.lazy(() => import("./components/admin/FeatureInsights"));
const QALearn = React.lazy(() => import("./components/learning/QALearn"));
const CertificateVerify = React.lazy(() => import("./components/learning/CertificateVerify"));
const AvatarSettings = React.lazy(() => import("./components/AvatarSettings"));
const ChangePassword = React.lazy(() => import("./components/ChangePassword"));
const EmailPreferences = React.lazy(() => import("./components/EmailPreferences"));
const UserManagement = React.lazy(() => import("./components/admin/UserManagement"));
const WelcomeWizard = React.lazy(() => import("./components/WelcomeWizard"));
const QRCodeGenerator = React.lazy(() => import("./components/QRCodeGenerator"));
const VisitorInsights = React.lazy(() => import("./components/VisitorInsights"));
const LocatePage = React.lazy(() => import("./components/LocatePage"));
import { VisitTracker } from "./utils/visitTracker";
import { installCrashReporter } from "./utils/crashReporter";
import { ErrorBoundary } from "./components/ErrorBoundary";
const CrashReports = React.lazy(() => import("./components/CrashReports"));

installCrashReporter();
const WhiteLabelDemo = React.lazy(() => import("./components/WhiteLabelDemo"));
const BrandedDemo = React.lazy(() => import("./components/BrandedDemo"));
const Accessibility = React.lazy(() => import("./components/Accessibility"));
const SitemapPage = React.lazy(() => import("./components/SitemapPage"));
const ComparePlans = React.lazy(() => import("./components/ComparePlans"));
const HealthHub = React.lazy(() => import("./components/health/HealthHub"));
const EmergencyID = React.lazy(() => import("./components/health/EmergencyID"));
const HealthShareView = React.lazy(() => import("./components/health/HealthShareView"));
const VirtualSessions = React.lazy(() => import("./components/VirtualSessions"));
const HealthInstitutions = React.lazy(() => import("./components/HealthInstitutions"));
const WorkflowsPage = React.lazy(() => import("./components/WorkflowsPage"));
const Arena = React.lazy(() => import("./components/Arena"));
const SupportChatWidget = React.lazy(() => import("./components/SupportChatWidget"));
const CreatorStudio = React.lazy(() => import("./components/studio/CreatorStudio"));
const CourseBuilder = React.lazy(() => import("./components/studio/CourseBuilder"));
const VaultTracker = React.lazy(() => import("./components/VaultTracker"));
const AIStudio = React.lazy(() => import("./components/AIStudio"));
const ProposalStudio = React.lazy(() => import("./components/ProposalStudio"));
const Matchmaking = React.lazy(() => import("./components/Matchmaking"));
const MediaStudio = React.lazy(() => import("./components/MediaStudio"));
const TapToPay = React.lazy(() => import("./components/TapToPay"));
const TapPayPublic = React.lazy(() => import("./components/TapPayPublic"));
const ChatFeedbackAdmin = React.lazy(() => import("./components/ChatFeedbackAdmin"));
const HeldPostsAdmin = React.lazy(() => import("./components/HeldPostsAdmin"));
const LegalDisclaimers = React.lazy(() => import("./components/LegalDisclaimers"));
const RefundPolicy = React.lazy(() => import("./components/RefundPolicy"));
const SecurityReportsAdmin = React.lazy(() => import("./components/SecurityReportsAdmin"));
const AboutPage = React.lazy(() => import("./components/AboutPage"));
const PressPage = React.lazy(() => import("./components/PressPage"));
const PayLanding = React.lazy(() => import("./components/PayLanding"));
const CredentialsStudio = React.lazy(() => import("./components/CredentialsStudio"));
const CredentialVerify = React.lazy(() => import("./components/CredentialVerify"));
const AuthCallback = React.lazy(() => import("./components/AuthCallback"));
const StaffLogin = React.lazy(() => import("./components/StaffLogin"));
const AdminLogin = React.lazy(() => import("./components/AdminLogin"));
const SupportDashboard = React.lazy(() => import("./components/SupportDashboard"));
const LearningPortal = React.lazy(() => import("./components/learning/LearningPortal"));
const InstitutionDashboard = React.lazy(() => import("./components/learning/InstitutionDashboard"));
const FranchiseConsole = React.lazy(() => import("./components/learning/FranchiseConsole"));
const PaymentReturn = React.lazy(() => import("./components/learning/PaymentReturn"));
const PaymentCancel = React.lazy(() => import("./components/learning/PaymentCancel"));
const StudentRegister = React.lazy(() => import("./components/learning/StudentRegister"));
const AcceptInvite = React.lazy(() => import("./components/learning/AcceptInvite"));
const LMSSubscribe = React.lazy(() => import("./components/learning/LMSSubscribe"));
const ComplianceCalendar = React.lazy(() => import("./components/learning/ComplianceCalendar"));
const DocumentVault = React.lazy(() => import("./components/learning/DocumentVault"));
const ProductsHub = React.lazy(() => import("./components/marketing/ProductsHub"));
const SuitePage = React.lazy(() => import("./components/marketing/SuitePage"));
const Connections = React.lazy(() => import("./components/Connections"));
const ConnectedApps = React.lazy(() => import("./components/ConnectedApps"));
const CourseCatalog = React.lazy(() => import("./components/courses/CourseCatalog"));
const CourseDetail = React.lazy(() => import("./components/courses/CourseDetail"));
const CoursePlayer = React.lazy(() => import("./components/courses/CoursePlayer"));
const CommunityRules = React.lazy(() => import("./components/CommunityRules"));
const SecurityCompliance = React.lazy(() => import("./components/SecurityCompliance"));
const HipaaNotice = React.lazy(() => import("./components/HipaaNotice"));
const DmcaPolicy = React.lazy(() => import("./components/DmcaPolicy"));
const FranchiseApply = React.lazy(() => import("./components/FranchiseApply"));
const FranchiseActivate = React.lazy(() => import("./components/FranchiseActivate"));
const EmailSignature = React.lazy(() => import("./components/EmailSignature"));
const NFCTagTester = React.lazy(() => import("./components/NFCTagTester"));
const EventsDiscover = React.lazy(() => import("./components/EventsDiscover"));
const EventNetworking = React.lazy(() => import("./components/EventNetworking"));
const ReferralLeaderboard = React.lazy(() => import("./components/ReferralLeaderboard"));
const ManageBilling = React.lazy(() => import("./components/ManageBilling"));
const QRStickerSheet = React.lazy(() => import("./components/QRStickerSheet"));
const CookieConsent = React.lazy(() => import("./components/CookieConsent").then((m) => ({ default: m.CookieConsent })));
const InstallPrompt = React.lazy(() => import("./components/InstallPrompt").then((m) => ({ default: m.InstallPrompt })));
const OfflineBanner = React.lazy(() => import("./components/OfflineBanner").then((m) => ({ default: m.OfflineBanner })));
const ExitIntentOffer = React.lazy(() => import("./components/ExitIntentOffer").then((m) => ({ default: m.ExitIntentOffer })));
const CanonicalTag = React.lazy(() => import("./components/CanonicalTag").then((m) => ({ default: m.CanonicalTag })));
const RobotsMeta = React.lazy(() => import("./components/RobotsMeta").then((m) => ({ default: m.RobotsMeta })));
const MetaAudit = React.lazy(() => import("./components/MetaAudit").then((m) => ({ default: m.MetaAudit })));
const PageSummary = React.lazy(() => import("./components/PageSummary").then((m) => ({ default: m.PageSummary })));
const UpdateAnnouncement = React.lazy(() => import("./components/UpdateAnnouncement").then((m) => ({ default: m.UpdateAnnouncement })));
import { GlobalFooter } from "./components/nav/AppFooter";
const PromoBanner = React.lazy(() => import("./components/PromoBanner").then((m) => ({ default: m.PromoBanner })));
const TrialBanner = React.lazy(() => import("./components/TrialBanner").then((m) => ({ default: m.TrialBanner })));
const RevenueDashboard = React.lazy(() => import("./components/admin/RevenueDashboard"));
const AdminEvents = React.lazy(() => import("./components/admin/AdminEvents"));
const PilotLeads = React.lazy(() => import("./components/admin/PilotLeads"));
const DeactivatedId = React.lazy(() => import("./components/DeactivatedId"));
const BlogPost = React.lazy(() => import("./components/Blog"));
const TeamMemberCard = React.lazy(() => import("./components/TeamMemberCard"));
const CardQrPoster = React.lazy(() => import("./components/CardQrPoster"));
const Crm = React.lazy(() => import("./components/Crm"));
const BlogList = React.lazy(() => import("./components/Blog").then((m) => ({ default: m.BlogList })));
const FreeNfcWriter = React.lazy(() => import("./components/FreeNfcWriter"));

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  // Enforce role isolation — block cross-access between portals
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    if (user.role === 'super_admin') return children; // master key: back-office access to every portal
    if (user.role === 'support') return <Navigate to="/staff" replace />;
    if (user.role === 'institution_admin' || user.role === 'instructor') return <Navigate to="/institution" replace />;
    if (user.role === 'student') return <Navigate to="/learn" replace />;
    if (user.role === 'investor') return <Navigate to="/investor" replace />;
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function App() {
  const [afterPaint, setAfterPaint] = React.useState(false);
  React.useEffect(() => {
    if ('requestIdleCallback' in window) {
      const id = window.requestIdleCallback(() => setAfterPaint(true), { timeout: 2500 });
      return () => window.cancelIdleCallback(id);
    }
    const t = setTimeout(() => setAfterPaint(true), 800);
    return () => clearTimeout(t);
  }, []);
  return (
    <HelmetProvider>
      <ErrorBoundary>
      <AuthProvider>
        <TenantProvider>
        <BrowserRouter>
        <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:bg-amber-400 focus:text-slate-900 focus:font-bold focus:px-4 focus:py-2 focus:rounded-lg" data-testid="skip-to-content">Skip to main content</a>
        <VisitTracker />
        {afterPaint && (
          <React.Suspense fallback={null}>
            <PromoBanner />
            <TrialBanner />
          </React.Suspense>
        )}
        <Toaster richColors position="top-right" />
        <ImpersonationBanner />
        <React.Suspense fallback={
          <div className="min-h-screen flex flex-col items-center justify-center" style={{ background: '#0f0f17' }}>
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-white font-black text-3xl mb-4" style={{ background: 'linear-gradient(135deg, #7c3aed, #c026d3)' }}>T</div>
            <div className="text-2xl font-extrabold tracking-tight" style={{ color: '#e2e8f0' }}>Tapn.vip</div>
            <div className="mt-6 w-9 h-9 rounded-full animate-spin" style={{ border: '3px solid rgba(245,158,11,0.2)', borderTopColor: '#f59e0b' }} />
          </div>
        }>
        <div id="main-content" tabIndex={-1} className="outline-none">
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/advertise" element={<Advertise />} />
          <Route path="/investors" element={<InvestorPortal />} />
          <Route path="/pitch" element={<PitchSummary />} />
          <Route path="/investors/dataroom/:appId" element={<InvestorDataRoom />} />
          <Route path="/graduates" element={<GradWall />} />
          <Route path="/nfc-test" element={<NFCTagTester />} />
          <Route path="/admin/investors" element={<InvestorAdmin />} />
          <Route path="/admin/insights" element={<FeatureInsights />} />
          <Route path="/learn/qa" element={<QALearn />} />
          <Route path="/certificate/:certNumber" element={<CertificateVerify />} />
          <Route path="/vc/:credId" element={<CredentialVerify />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/staff/login" element={<StaffLogin />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/franchise/apply" element={<FranchiseApply />} />
          <Route path="/franchise/activate" element={<FranchiseActivate />} />
          <Route path="/signature" element={<ProtectedRoute><EmailSignature /></ProtectedRoute>} />
          <Route path="/invite/:token" element={<AcceptInvite />} />
          <Route path="/lms-subscribe" element={<ProtectedRoute><LMSSubscribe /></ProtectedRoute>} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/team-member/setup" element={<TeamMemberSetup />} />
          <Route path="/team" element={<ProtectedRoute><TeamManagement /></ProtectedRoute>} />
          <Route path="/pitch/liberty-bank" element={<PitchLibertyBank />} />
          <Route path="/pitch/enterprise" element={<PitchLibertyBank />} />
          <Route path="/pitch/county-ems" element={<PitchCountyEms />} />
          <Route path="/pitch/milwaukee-dhhs" element={<PitchMilwaukeeDhhs />} />
          <Route path="/folder/:slug" element={<ResourceFolder />} />
          <Route path="/admin/folders" element={<ProtectedRoute allowedRoles={['super_admin']}><div className="min-h-screen bg-[#0a1220] p-4 md:p-8"><div className="max-w-4xl mx-auto"><ResourceFoldersAdmin /></div></div></ProtectedRoute>} />
          <Route path="/pitch/event-checkin" element={<PitchEventCheckin />} />
          <Route path="/pitch/digital-card" element={<PitchDigitalCard />} />
          <Route path="/team/dashboard" element={<TeamMemberDashboard />} />
          <Route path="/family-tracker" element={<ProtectedRoute><FamilyTracker /></ProtectedRoute>} />
          <Route path="/session-poster/:eventId/:sessionId" element={<SessionPoster />} />
          <Route path="/session-checkin/:eventId/:sessionId" element={<SessionCheckin />} />
          <Route path="/terms-team-member" element={<TermsTeamMember />} />
          <Route path="/p/:slug" element={<PublicProfile />} />
          <Route path="/products" element={<ProductsHub />} />
          <Route path="/products/:slug" element={<SuitePage />} />
          <Route path="/connections" element={<ProtectedRoute><Connections /></ProtectedRoute>} />
          <Route path="/connected-apps" element={<ProtectedRoute><ConnectedApps /></ProtectedRoute>} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/paypal/return" element={<PayPalReturn />} />
          <Route path="/institutional" element={<InstitutionalLicensing />} />
          <Route path="/compare" element={<ComparePlans />} />
          <Route path="/health" element={<ProtectedRoute><HealthHub /></ProtectedRoute>} />
          <Route path="/medical/:token" element={<EmergencyID />} />
          <Route path="/sessions" element={<VirtualSessions />} />
          <Route path="/health-institutions" element={<HealthInstitutions />} />
          <Route path="/guides" element={<WorkflowsPage />} />
          <Route path="/arena" element={<Arena />} />
          <Route path="/courses" element={<CourseCatalog />} />
          <Route path="/courses/:id" element={<CourseDetail />} />
          <Route path="/courses/:id/learn" element={<ProtectedRoute><CoursePlayer /></ProtectedRoute>} />
          <Route path="/studio" element={<ProtectedRoute allowedRoles={['super_admin','institution_admin','instructor']}><CreatorStudio /></ProtectedRoute>} />
          <Route path="/studio/courses/:id" element={<ProtectedRoute allowedRoles={['super_admin','institution_admin','instructor']}><CourseBuilder /></ProtectedRoute>} />
          <Route path="/bundle" element={<BundleFunnel />} />
          <Route path="/community" element={<Community />} />
          <Route path="/spotlight" element={<FeatureSpotlight />} />
          <Route path="/government-security" element={<GovernmentSecurity />} />
          <Route path="/digital-identity" element={<DigitalIdentity />} />
          <Route path="/trust" element={<TrustCenter />} />
          <Route path="/verify-identity" element={<ProtectedRoute><VerifyIdentity /></ProtectedRoute>} />
          <Route path="/integrations" element={<ProtectedRoute><IntegrationsHub /></ProtectedRoute>} />
          <Route path="/admin/compliance" element={<ProtectedRoute allowedRoles={['super_admin']}><div className="min-h-screen bg-[#0a1220] p-4 md:p-8"><div className="max-w-5xl mx-auto"><ComplianceCenter /></div></div></ProtectedRoute>} />
          <Route path="/gate-setup" element={<GateDeviceGuide />} />
          <Route path="/community-rules" element={<CommunityRules />} />
          <Route path="/security" element={<SecurityCompliance />} />
          <Route path="/vault-plans" element={<ProtectedRoute><VaultPlans /></ProtectedRoute>} />
          <Route path="/b2b" element={<ProtectedRoute><B2BHub /></ProtectedRoute>} />
          <Route path="/admin/b2b" element={<ProtectedRoute allowedRoles={['super_admin']}><B2BConsole /></ProtectedRoute>} />
          <Route path="/investor" element={<ProtectedRoute allowedRoles={['investor','super_admin']}><InvestorDashboard /></ProtectedRoute>} />
          <Route path="/e/:slug/network" element={<EventNetworking />} />
          <Route path="/e/:slug" element={<PublicEventRegister />} />
          <Route path="/events/login" element={<EventsLogin />} />
          <Route path="/events/staff" element={<EventStaffCheckin />} />
          <Route path="/events/ticket" element={<AttendeeTicket />} />
          <Route path="/events/discover" element={<EventsDiscover />} />
          <Route path="/leaderboard" element={<ReferralLeaderboard />} />
          <Route path="/admin/revenue" element={<ProtectedRoute><RevenueDashboard /></ProtectedRoute>} />
          <Route path="/admin/events" element={<ProtectedRoute><AdminEvents /></ProtectedRoute>} />
          <Route path="/admin/pilot-leads" element={<ProtectedRoute><PilotLeads /></ProtectedRoute>} />
          <Route path="/id/deactivated" element={<DeactivatedId />} />
          <Route path="/free-nfc-writer" element={<FreeNfcWriter />} />
          <Route path="/blog" element={<BlogList />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/t/:username" element={<TeamMemberCard />} />
          <Route path="/t/:username/poster" element={<CardQrPoster />} />
          <Route path="/crm" element={<ProtectedRoute><Crm /></ProtectedRoute>} />
          <Route path="/events" element={<ProtectedRoute><EventsHub /></ProtectedRoute>} />
          <Route path="/events/:id" element={<ProtectedRoute><EventManager /></ProtectedRoute>} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/locate/:slug" element={<LocatePage />} />
          <Route path="/white-label" element={<WhiteLabelDemo />} />
          <Route path="/demo/:slug" element={<BrandedDemo />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/privacy-policy" element={<Navigate to="/privacy" replace />} />
          <Route path="/privacy/export" element={<Navigate to="/privacy-settings" replace />} />
          <Route path="/privacy/delete" element={<Navigate to="/privacy-settings" replace />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/cookies" element={<Navigate to="/cookie-policy" replace />} />
          <Route path="/search" element={<SiteSearch />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/tos" element={<Navigate to="/terms" replace />} />
          <Route path="/support" element={<Support />} />
          <Route path="/accessibility" element={<Accessibility />} />
          <Route path="/hipaa" element={<HipaaNotice />} />
          <Route path="/dmca" element={<DmcaPolicy />} />
          <Route path="/sitemap" element={<SitemapPage />} />
          <Route path="/for/:vertical" element={<WedgePage />} />
          <Route path="/academy" element={<Navigate to="/for/academy" replace />} />
          <Route path="/admin/roles" element={<ProtectedRoute><RolesPermissions /></ProtectedRoute>} />
          <Route path="/admin/audit" element={<ProtectedRoute><AuditLog /></ProtectedRoute>} />
          <Route path="/admin/visitors" element={<ProtectedRoute><VisitorInsights /></ProtectedRoute>} />
          <Route path="/admin/errors" element={<ProtectedRoute><CrashReports /></ProtectedRoute>} />
          <Route path="/org-admin" element={<ProtectedRoute><OrgAdmin /></ProtectedRoute>} />
          <Route
            path="/enterprise"
            element={
              <ProtectedRoute>
                <EnterpriseDashboard />
              </ProtectedRoute>
            }
          />

          {/* Primary Feature - Reels (sign-in required) */}
          <Route path="/reels" element={<ProtectedRoute><Reels /></ProtectedRoute>} />
          <Route path="/create-reel" element={<ProtectedRoute><CreateReel /></ProtectedRoute>} />

          {/* Protected Social Routes */}
          <Route
            path="/feed"
            element={
              <ProtectedRoute>
                <Feed />
              </ProtectedRoute>
            }
          />
          <Route
            path="/messages"
            element={
              <ProtectedRoute>
                <Messages />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/avatar-settings"
            element={
              <ProtectedRoute>
                <AvatarSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/change-password"
            element={
              <ProtectedRoute>
                <ChangePassword />
              </ProtectedRoute>
            }
          />
          <Route
            path="/qr-code"
            element={
              <ProtectedRoute>
                <QRCodeGenerator />
              </ProtectedRoute>
            }
          />
          <Route
            path="/vault"
            element={
              <ProtectedRoute>
                <VaultTracker />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ai-studio"
            element={
              <ProtectedRoute>
                <AIStudio />
              </ProtectedRoute>
            }
          />
          <Route path="/proposals" element={<ProtectedRoute><ProposalStudio /></ProtectedRoute>} />
          <Route path="/matchmaking" element={<ProtectedRoute><Matchmaking /></ProtectedRoute>} />
          <Route path="/media-studio" element={<ProtectedRoute><MediaStudio /></ProtectedRoute>} />
          <Route path="/tap-pay" element={<ProtectedRoute><TapToPay /></ProtectedRoute>} />
          <Route path="/pay/:slug" element={<TapPayPublic />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/press" element={<PressPage />} />
          <Route path="/pay" element={<PayLanding />} />
          <Route path="/admin/chat-feedback" element={<ProtectedRoute><ChatFeedbackAdmin /></ProtectedRoute>} />
          <Route path="/admin/moderation" element={<ProtectedRoute><HeldPostsAdmin /></ProtectedRoute>} />
          <Route path="/disclaimers" element={<LegalDisclaimers />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />
          <Route path="/admin/security-reports" element={<ProtectedRoute><SecurityReportsAdmin /></ProtectedRoute>} />
          <Route path="/credentials" element={<ProtectedRoute><CredentialsStudio /></ProtectedRoute>} />
          <Route
            path="/email-preferences"
            element={
              <ProtectedRoute>
                <EmailPreferences />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/user-management"
            element={
              <ProtectedRoute>
                <UserManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/welcome"
            element={
              <ProtectedRoute>
                <WelcomeWizard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/create-profile"
            element={
              <ProtectedRoute>
                <CreateProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/share/:slug"
            element={
              <ProtectedRoute>
                <ShareProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/privacy-settings"
            element={
              <ProtectedRoute>
                <PrivacySettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile/customize/:profileId"
            element={
              <ProtectedRoute>
                <ProfileCustomization />
              </ProtectedRoute>
            }
          />
          <Route
            path="/subscription-upgrade"
            element={
              <ProtectedRoute>
                <SubscriptionUpgrade />
              </ProtectedRoute>
            }
          />
          <Route
            path="/billing"
            element={
              <ProtectedRoute>
                <ManageBilling />
              </ProtectedRoute>
            }
          />
          <Route
            path="/qr-stickers"
            element={
              <ProtectedRoute>
                <QRStickerSheet />
              </ProtectedRoute>
            }
          />
          <Route
            path="/super-admin"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <SuperAdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/continuity"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <ContinuitySettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/security"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <SecurityMonitor />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/knowledge-base"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <KnowledgeBase />
              </ProtectedRoute>
            }
          />
          <Route
            path="/staff"
            element={
              <ProtectedRoute allowedRoles={['support', 'super_admin']}>
                <SupportDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/learn"
            element={
              <ProtectedRoute allowedRoles={['student']}>
                <LearningPortal />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-vault"
            element={
              <ProtectedRoute allowedRoles={['student']}>
                <DocumentVault />
              </ProtectedRoute>
            }
          />
          <Route
            path="/institution"
            element={
              <ProtectedRoute allowedRoles={['institution_admin', 'instructor']}>
                <InstitutionDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/franchise"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <FranchiseConsole />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payment/return"
            element={
              <ProtectedRoute>
                <PaymentReturn />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payment/cancel"
            element={
              <ProtectedRoute>
                <PaymentCancel />
              </ProtectedRoute>
            }
          />
          <Route path="/join" element={<StudentRegister />} />
          <Route
            path="/compliance"
            element={
              <ProtectedRoute>
                <ComplianceCalendar />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/geofencing"
            element={
              <ProtectedRoute>
                <GeofencingAnalytics />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/social-listening"
            element={
              <ProtectedRoute>
                <SocialListeningAnalytics />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/marketplace"
            element={
              <ProtectedRoute>
                <MarketplaceAnalytics />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <ProtectedRoute>
                <PlatformSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/subscriptions"
            element={
              <ProtectedRoute>
                <SubscriptionManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/white-label"
            element={
              <ProtectedRoute>
                <WhiteLabelManagement />
              </ProtectedRoute>
            }
          />
          <Route path="/welcome-tour" element={<WelcomeTour />} />
          <Route path="/install" element={<InstallApp />} />
          <Route
            path="/admin/tenants"
            element={
              <ProtectedRoute>
                <TenantManager />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/cms"
            element={
              <ProtectedRoute>
                <CMSManager />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/ads"
            element={
              <ProtectedRoute>
                <AdManager />
              </ProtectedRoute>
            }
          />
          <Route
            path="/notifications"
            element={
              <ProtectedRoute>
                <NotificationSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/data-mining"
            element={
              <ProtectedRoute>
                <DataMining />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/geofencing-listening"
            element={
              <ProtectedRoute>
                <GeofencingSocialListening />
              </ProtectedRoute>
            }
          />
          <Route
            path="/subscription-success"
            element={
              <ProtectedRoute>
                <SubscriptionSuccess />
              </ProtectedRoute>
            }
          />
        </Routes>
        </div>
        </React.Suspense>
        <GlobalFooter />
        {afterPaint && (
          <React.Suspense fallback={null}>
            <PageSummary />
            <CookieConsent />
            <InstallPrompt />
            <OfflineBanner />
            <ExitIntentOffer />
            <CanonicalTag />
            <RobotsMeta />
            <MetaAudit />
            <SupportChatWidget />
            <UpdateAnnouncement />
          </React.Suspense>
        )}
        </BrowserRouter>
        </TenantProvider>
      </AuthProvider>
      </ErrorBoundary>
    </HelmetProvider>
  );
}

export default App;
